import { Errors } from '../enums/errors';
import { LicenseError } from '../error/license';
const LICENSE_URL_ENDPOINT = '@webgate/melody/v1/license_url';
const EXPIRY_THRESHOLD = 60000;
/**
 * Used for resolving signed URLs to the license server.
 */
export class LicenseURLResolver {
    /**
     *
     * @constructor
     * @param options - The options for the instance.
     */
    constructor(options) {
        /**
         * A string identifying the SDK version of the library wrapping playback.
         */
        this._sdk = {
            name: '',
            version: '',
        };
        /**
         * The security level for the required licenses.
         */
        this._securityLevel = null;
        /**
         * A map containing the last known valid signed urls.
         *
         * Entries are keyed in the format `<keysystem>:<mediatype>`.
         */
        this._urls = {};
        if (options.sdk) {
            this._sdk = options.sdk;
        }
        if (options.securityLevel) {
            this._securityLevel = options.securityLevel;
        }
        this._transport = options.transport;
        this._transport.on(this._transport.EVENT_CONNECTION_ID, () => {
            // We assume that when we get a new connection id, we will have a new user,
            // so we don't want to re-use the old urls, which are tied to a different
            // user. We just clear them as is.
            this._urls = {};
        });
    }
    /**
     * Fetches a signed license URL for a particular keysystem and mediatype combo.
     *
     * @param keySystem - The key system for the media.
     * @param mediaType - The type of media, can be "audio" or "video".
     * @return A promise containing the string URL.
     */
    get(keySystem, mediaType) {
        const key = `${keySystem}:${mediaType}`;
        const cached = this._urls[key];
        if (cached && cached.expires > Date.now() + EXPIRY_THRESHOLD) {
            return Promise.resolve(cached.uri);
        }
        const url = `${LICENSE_URL_ENDPOINT}?keysystem=${keySystem}&mediatype=${mediaType}&sdk_name=${this._sdk.name}&sdk_version=${this._sdk.version}`;
        return this._transport
            .request(url, {
            responseType: 'json',
            retry: {
                condition: (resp, StatusFamily) => {
                    const statusFamily = resp.getStatusFamily();
                    return (statusFamily === StatusFamily.SERVER_ERROR ||
                        statusFamily === StatusFamily.CONNECTION_ERROR);
                },
            },
        })
            .then((response) => {
            const result = response.body;
            // Successful
            if (result && response.status === 200) {
                // Expiry is in seconds, turn it into ms.
                result.expires *= 1000;
                result.uri = `@webgate/${result.uri}`;
                if (this._securityLevel) {
                    result.uri += [
                        result.uri.indexOf('?') !== -1 ? '&' : '?',
                        'sl=',
                        this._securityLevel,
                    ].join('');
                }
                this._urls[key] = result;
                return result.uri;
            }
            // Errors
            let error;
            if (!result) {
                error = LicenseError.fatal(Errors.LICENSE_RESOLVE_INVALID_RESPONSE, 'License URL endpoint responded with invalid response');
            }
            else if (response.status === 400 &&
                result &&
                result.code === 'deprecated-version') {
                error = LicenseError.fatal(Errors.LICENSE_RESOLVER_DEPRECATED_VERSION, 'This version of the SDK is no longer supported. Please upgrade');
            }
            else {
                error = new LicenseError(Errors.LICENSE_RESOLVER_CANT_RESOLVE_URL, `License URL endpoint responded with status ${response.status}`);
            }
            error.status = response.status;
            return Promise.reject(error);
        });
    }
    /**
     * Removes a signed license for a particular keysystem and mediatype.
     *
     * @param keySystem - The key system for the media.
     * @param mediaType - The type of media, can be "audio" or "video".
     */
    remove(keySystem, mediaType) {
        this._urls[`${keySystem}:${mediaType}`] = null;
    }
}
//# sourceMappingURL=license_url_resolver.js.map